import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import handleClearError from "../../../core/methods/handleClearError";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import ErrorMessage from "../../../core/common/ErrorMessage";
import webservice_url from "../../../core/webservice/Webservice_url";
import useApi from "../../../core/customhook/useApi";
import { toast } from "react-toastify";

// ── Types ──────────────────────────────────────────────────────────────────────
type PasswordField = "newPassword" | "confirmPassword";

interface ChangePasswordForm {
  email: string;
  newPassword: string;
  confirmPassword: string;
}

// ── Component ──────────────────────────────────────────────────────────────────

const ChangePassword = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const location = useLocation();

  const navigateemail = location.state?.passemail ?? "";

  const [formData, setFormData] = useState<ChangePasswordForm>({
    email: navigateemail,
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [passwordVisibility, setPasswordVisibility] = useState<
    Record<PasswordField, boolean>
  >({
    newPassword: false,
    confirmPassword: false,
  });

  // API
  const resetPassword = useApi<any>(webservice_url.reset_password, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  // Pre-fill email from local-storage
  useEffect(() => {
    const userMail = localStorage.getItem("user-data");
    if (!!userMail) {
      setFormData((prev) => ({ ...prev, email: userMail }));
    }
  }, []);

  // ── helpers ──────────────────────────────────────────────────────────────────
  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value?.trim() }));
    handleClearError(setErrors, name);
  };

  // ── validation ────────────────────────────────────────────────────────────────
  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.newPassword?.trim()) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword?.trim()) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---- handleResetPassword ----
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const res = await resetPassword.callApi({
        email: navigateemail ?? "",
        new_password: formData?.newPassword ?? "",
      });
      if (res?.success) {
        toast.success(res?.message || "Reset Successfully");
        navigate(routes.login, { replace: true });
      }
    } catch (error: any) {
      console.error("login failed:", error?.response);
      const errorMsg = error?.response?.data?.error ?? "Invalid Credentials";
      toast.error(errorMsg);
    }
  };
  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <div className="container-fluid vh-100 overflow-hidden p-0">
      <div className="row h-100 g-0">
        <div className="col-lg-7 d-none d-lg-flex align-items-center justify-content-center position-relative">
          <div
            className="position-absolute top-0 start-0 h-100 w-100 opacity-25"
            style={{ border: "none", background: "black" }}
          ></div>
          <ImageWithBasePath
            src="assets/img/login_image.png"
            alt="Img"
            className="object-fit-cover w-100 h-100 position-relative"
          />
        </div>
        <div className="col-12 col-lg-5 h-100 overflow-auto d-flex align-items-center justify-content-center ">
          <div className="w-100">
            <form onSubmit={handleResetPassword}>
              <div className="d-flex flex-column align-items-center gap-3 px-3 px-sm-4 py-4 pb-0">
                {/* Logo */}
                <div
                  className="mx-auto d-inline text-center"
                  style={{ maxWidth: 190 }}
                >
                  <ImageWithBasePath
                    src="/assets/img/logo.png"
                    className="img-fluid"
                    alt="Logo"
                  />
                </div>

                <div className="card py-4 px-4 px-sm-4 rounded-4 border w-100">
                  <div className="text-center mb-3">
                    <h2 className="mb-2">Change Password</h2>
                    <p className="mb-0">Set a new password for your account</p>
                  </div>

                  {/* Email — read-only, prefilled from query param */}
                  <div className="mb-3">
                    <label className="form-label">Email Address</label>
                    <div className="input-group mb-1">
                      <input
                        type="email"
                        className="form-control border-end-0"
                        name="email"
                        value={navigateemail}
                        disabled
                      />
                      <span className="input-group-text border-start-0">
                        <i className="ti ti-mail" />
                      </span>
                    </div>
                  </div>
                  {/* New Password */}
                  <div className="mb-3">
                    <label className="form-label">New Password</label>
                    <div className="pass-group mb-1">
                      <input
                        type={
                          passwordVisibility.newPassword ? "text" : "password"
                        }
                        className="pass-input form-control"
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        placeholder="Enter new password"
                        disabled={resetPassword.loading}
                      />
                      <span
                        className={`ti toggle-passwords ${
                          passwordVisibility.newPassword
                            ? "ti-eye"
                            : "ti-eye-off"
                        }`}
                        onClick={() => togglePasswordVisibility("newPassword")}
                      />
                    </div>
                    <ErrorMessage message={errors?.newPassword} />
                  </div>

                  {/* Confirm Password */}
                  <div className="mb-3">
                    <label className="form-label">Confirm Password</label>
                    <div className="pass-group mb-1">
                      <input
                        type={
                          passwordVisibility.confirmPassword
                            ? "text"
                            : "password"
                        }
                        className="pass-input form-control"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Confirm new password"
                        disabled={resetPassword.loading}
                      />
                      <span
                        className={`ti toggle-passwords ${
                          passwordVisibility.confirmPassword
                            ? "ti-eye"
                            : "ti-eye-off"
                        }`}
                        onClick={() =>
                          togglePasswordVisibility("confirmPassword")
                        }
                      />
                    </div>
                    <ErrorMessage message={errors?.confirmPassword} />
                  </div>

                  {/* Submit */}
                  <div className="mb-3 mt-0">
                    <button
                      type="submit"
                      className="btn btn-primary w-100 "
                      disabled={resetPassword.loading}
                    >
                      {resetPassword.loading
                        ? "Updating Password…"
                        : "Update Password"}
                    </button>
                  </div>

                  {/* Back to login */}
                  <div className="text-center">
                    <Link
                      to={all_routes.login}
                      className="link-secondary small"
                    >
                      <i className="ti ti-arrow-left" /> Back to Sign In
                    </Link>
                  </div>
                </div>

                <div className="mt-3 pb-4 text-center">
                  <p className="mb-0 text-gray-9">
                    © 2026 Aupertz. All rights reserved.
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
